import { Text, View, type StyleProp, type ViewStyle } from "react-native";
import { AlertTriangle, CloudOff, RotateCcw } from "lucide-react-native";
import { CompactActionButton } from "./CompactActionButton";
import {
  colors,
  radii,
  semanticColors,
  spacing,
  typography,
} from "../theme/tokens";
import { cardSurface } from "./CardSurface";

const DARK_GLASS_CARD_STYLE: ViewStyle = {
  borderRadius: radii.lg,
  backgroundColor: semanticColors.onDark.fill.subtle,
  borderWidth: 1,
  borderColor: semanticColors.onDark.fill.subtle,
  shadowColor: "#000",
  shadowOffset: { width: 0, height: 8 },
  shadowOpacity: 0.16,
  shadowRadius: 32,
};

type ClinicalRetryButtonProps = {
  retryLabel: string;
  retryingLabel: string;
  onRetry: () => void;
  isRetrying: boolean;
  dark: boolean;
  style?: StyleProp<ViewStyle>;
};

/**
 * The app's one "Try again": the compact tonal button, with the retry icon.
 * On a dark surface the same pill takes a translucent light fill, because the
 * tonal brown does not read against brown.
 */
function ClinicalRetryButton({
  retryLabel,
  retryingLabel,
  onRetry,
  isRetrying,
  dark,
  style,
}: ClinicalRetryButtonProps) {
  const label = isRetrying ? retryingLabel : retryLabel;
  const contentColor = dark
    ? semanticColors.onDark.text.primary
    : semanticColors.action.tonal.foreground;

  return (
    <CompactActionButton
      label={label}
      accessibilityLabel={label}
      action="clinical_data.retry"
      onPress={onRetry}
      loading={isRetrying}
      size="md"
      variant="primary"
      leading={<RotateCcw size={13} color={contentColor} strokeWidth={1.9} />}
      activityIndicatorColor={contentColor}
      pressedOpacity={dark ? 0.7 : undefined}
      style={[dark ? ON_DARK_RETRY_STYLE : null, style]}
      textStyle={dark ? { color: contentColor } : undefined}
    />
  );
}

const ON_DARK_RETRY_STYLE: ViewStyle = {
  backgroundColor: semanticColors.onDark.fill.subtle,
};

export type ClinicalDataStatusProps = {
  state: "unavailable" | "stale";
  title?: string;
  message: string;
  retryLabel: string;
  retryingLabel: string;
  onRetry: () => void;
  isRetrying?: boolean;
  appearance?: "light" | "dark";
  /** Removes card chrome when the status is already inside a glass/card surface. */
  embedded?: boolean;
  style?: StyleProp<ViewStyle>;
  testID?: string;
};

/** Shared unavailable/stale-with-warning treatment for clinical records. */
export function ClinicalDataStatus({
  state,
  title,
  message,
  retryLabel,
  retryingLabel,
  onRetry,
  isRetrying = false,
  appearance = "light",
  embedded = false,
  style,
  testID,
}: ClinicalDataStatusProps) {
  const dark = appearance === "dark";
  const accent = dark ? colors.warning : colors.text;
  const primaryText = dark
    ? semanticColors.onDark.text.primary
    : semanticColors.text.primary;
  const secondaryText = dark
    ? semanticColors.onDark.text.secondary
    : semanticColors.text.tertiary;
  const glassCardStyle = dark
    ? DARK_GLASS_CARD_STYLE
    : (cardSurface() as ViewStyle);
  const Icon = state === "unavailable" ? CloudOff : AlertTriangle;

  if (state === "stale") {
    return (
      <View
        testID={testID}
        accessibilityLiveRegion="polite"
        style={[
          glassCardStyle,
          {
            flexDirection: "row",
            alignItems: "center",
            gap: spacing.sm,
            paddingHorizontal: spacing.md,
            paddingVertical: spacing.sm,
          },
          style,
        ]}
      >
        {/* Stale data is a warning: the yellow triangle, as on a notice. */}
        <Icon
          size={16}
          color={semanticColors.status.warning}
          strokeWidth={1.8}
        />
        <Text
          style={{
            flex: 1,
            fontFamily: typography.families.sans,
            fontSize: 12,
            lineHeight: 17,
            color: secondaryText,
          }}
        >
          {message}
        </Text>
        <ClinicalRetryButton
          retryLabel={retryLabel}
          retryingLabel={retryingLabel}
          onRetry={onRetry}
          isRetrying={isRetrying}
          dark={dark}
        />
      </View>
    );
  }

  return (
    <View
      testID={testID}
      accessibilityLiveRegion="polite"
      style={[
        embedded ? null : glassCardStyle,
        {
          alignItems: "center",
          paddingHorizontal: spacing.xl,
          paddingVertical: spacing["3xl"],
        },
        style,
      ]}
    >
      <CloudOff
        size={23}
        color={accent}
        strokeWidth={1.6}
        style={{ marginBottom: spacing.md }}
      />
      {title ? (
        <Text
          style={{
            fontFamily: typography.families.sansMedium,
            fontSize: 16,
            lineHeight: 21,
            color: primaryText,
            textAlign: "center",
          }}
        >
          {title}
        </Text>
      ) : null}
      <Text
        style={{
          maxWidth: 360,
          marginTop: title ? spacing.sm : 0,
          fontFamily: typography.families.sans,
          fontSize: 13,
          lineHeight: 19,
          color: secondaryText,
          textAlign: "center",
        }}
      >
        {message}
      </Text>
      <ClinicalRetryButton
        retryLabel={retryLabel}
        retryingLabel={retryingLabel}
        onRetry={onRetry}
        isRetrying={isRetrying}
        dark={dark}
        style={{ marginTop: spacing.lg }}
      />
    </View>
  );
}
